import { useCallback, MouseEvent, memo } from 'react';
import { Localize, useTranslate } from '@agilepoint/wat-react';
import { FlexBox, ICheckboxGroupItemProps, ICheckboxGroupOption, Icon, StackPanel, Typography } from '@agilepoint/wat-react-ux';
import { StyledStackPanel } from 'common';
import { AuthenicationTypeWrapper, AuthenticationProviderIcon, AuthenicationProvidersGroup, UncheckedState, ScrollableStackPanel } from '../styled-components';
import { SelectedAuthContentForm } from './selected-auth-content-form';
import { useKeyboardAccessibility } from 'common/hooks';

interface IAuthenticationFormProps {
    optionsData: ICheckboxGroupOption[];
    onMarkAsDefault: (key: React.Key) => void;
    containerHeight?: string;
    headerLabel?: string;
}

export const AuthenticationForm = (props: IAuthenticationFormProps) => {
    const { onMarkAsDefault, optionsData, containerHeight, headerLabel } = props;
    const translate = useTranslate();
    const onRenderItem = useCallback((args: ICheckboxGroupItemProps) => (<AuthenicationType {...args} onMarkAsDefault={onMarkAsDefault} />), [onMarkAsDefault]);

    const validate = useCallback((value: string[]) => {
        if (value.length === 0) {
            return translate('modules.createNewApp.steps.authentication.authValidateText');
        }
        const markedAsDefaultProvider = optionsData.find((_item) => _item.value.markedAsDefault);
        if (!value.includes(markedAsDefaultProvider?.key as string)) {
            return translate('modules.createNewApp.steps.authentication.authSecondaryValidationText');
        }
        return true;
    }, [optionsData, translate]);

    return (
        <StyledStackPanel margin='30px 0px 0px 0px' gap="30" height={containerHeight ? containerHeight : 'calc(100% - 111px)'} width="100%" orientation='vertical' alignment="center">
            <StackPanel gap="10" orientation='vertical' alignment="center" width='100%'>
                <Typography.H2 title={headerLabel ? headerLabel : translate('modules.createNewApp.steps.authentication.headerLabel')}>
                    {headerLabel ? headerLabel : <Localize translate='modules.createNewApp.steps.authentication.headerLabel' />}
                </Typography.H2>
                <Typography.Medium title={translate('modules.createNewApp.steps.authentication.subHeaderLabel')}><Localize translate='modules.createNewApp.steps.authentication.subHeaderLabel' /></Typography.Medium>
            </StackPanel>
            <ScrollableStackPanel orientation='vertical' gap="40" alignment="center" width="100%" height="calc(100% - 90px)">
                <AuthenicationProvidersGroup
                    groupName="authentication-providers"
                    name="authentication"
                    options={optionsData}
                    fieldOrientation="vertical"
                    validationRules={{ validate: validate }}
                    onRenderItem={onRenderItem} />
                <SelectedAuthContentForm />
            </ScrollableStackPanel>
        </StyledStackPanel>
    );
};

const onClick = (ev: MouseEvent<HTMLDivElement>) => ev.stopPropagation();

const AuthenicationType = memo((props: ICheckboxGroupItemProps & { onMarkAsDefault: (key: React.Key) => void }) => {
    const { itemData: { name, value, key }, isSelected, toggleSelection, onMarkAsDefault } = props;
    const translate = useTranslate();
    const onUnCheckClick = useCallback(() => onMarkAsDefault(key), [key, onMarkAsDefault]);
    const { elementRef } = useKeyboardAccessibility<HTMLDivElement>({ callback: toggleSelection });
    const { elementRef: uncheckedStateRef } = useKeyboardAccessibility<HTMLSpanElement>({ callback: onUnCheckClick, stopPropagation: true });
    const renderSelectionState = () => (
        value.markedAsDefault ?
            <Icon
                className="ap-RadioChoice-Icon"
                iconSize='20'
                iconName="ap-thick-checkmark.solid" />
            :
            <UncheckedState
                $size='20px'
                tabIndex={0}
                ref={uncheckedStateRef}
                onClick={onUnCheckClick}
                $isActive={isSelected === true ? isSelected : false} />
    );

    return (
        <AuthenicationTypeWrapper flexDirection='column' tabIndex={0} ref={elementRef} $isSelected={isSelected} onClick={toggleSelection}>
            <StackPanel justifyContent="flex-end" height='20px' width="100%" gap="10" alignment="center" onClick={onClick}>
                {
                    isSelected || value.markedAsDefault ?
                        <>
                            <Typography.Small title={translate('modules.createNewApp.steps.authentication.markDefaultTitle')}><Localize translate='modules.createNewApp.steps.authentication.markDefaultTitle' /></Typography.Small>
                            {renderSelectionState()}
                        </>
                        : <></>
                }
            </StackPanel>
            <FlexBox flexDirection='column' alignItems="center" height='calc(100% - 20px)' justifyContent="center">
                <AuthenticationProviderIcon iconName={value.iconName} iconSize="36" />
                <Typography.Medium title={name}>{name}</Typography.Medium>
            </FlexBox>
        </AuthenicationTypeWrapper>
    );
});
